/**
 * TodayPanel -- AI-prioritized view showing MUST-DO and SHOULD-DO sections.
 *
 * When todayPlan is provided (from GET /today API):
 * - Shows warnings at the top if any
 * - MUST-DO section with top priority tasks and AI reasoning
 * - SHOULD-DO section with secondary priority tasks
 *
 * Fallback behavior (no todayPlan):
 * - Unified timeline of today's tasks and events sorted by time
 */

import type { SidebarTask, SidebarEvent, TodayPlan, UserProfile } from "./types";
import { WelcomeCard } from "../../onboarding/welcome-card";

interface TodayPanelProps {
  tasks: SidebarTask[];
  events: SidebarEvent[];
  todayPlan?: TodayPlan;
  profile?: UserProfile | null;
  onTaskClick: (task: SidebarTask) => void;
  onEventClick: (event: SidebarEvent) => void;
  onOpenChat?: () => void;
  onOpenWizard?: () => void;
}

type TimelineItem =
  | { type: "task"; data: SidebarTask; time: Date | null }
  | { type: "event"; data: SidebarEvent; time: Date };

function isToday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-priority-critical",
  high: "bg-priority-high",
  medium: "bg-priority-medium",
  low: "bg-priority-low",
};

/**
 * Renders a single task item with optional reasoning
 */
function TaskItem({
  task,
  time,
  showReasoning,
  onClick,
}: {
  task: SidebarTask;
  time?: Date | null;
  showReasoning?: boolean;
  onClick: () => void;
}): React.ReactElement {
  const priorityColor = PRIORITY_COLORS[task.priority ?? ""] ?? "bg-text-tertiary";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors duration-100 cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        {/* Checkbox circle */}
        <div className="mt-0.5 flex-shrink-0">
          <div className={`w-4 h-4 rounded-full border-2 border-current ${priorityColor.replace('bg-', 'text-')} group-hover:bg-surface`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-text-primary truncate">{task.title}</p>
            {task.score != null && (
              <span className="flex-shrink-0 text-xs font-mono text-primary bg-primary-subtle px-1.5 rounded"
                    title="AI priority score">
                {task.score}
              </span>
            )}
          </div>
          {showReasoning && task.reasoning && (
            <p className="text-xs text-text-tertiary mt-0.5 line-clamp-2">{task.reasoning}</p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {time && (
              <span className="text-xs font-mono text-text-tertiary">
                {formatTime(time)}
              </span>
            )}
            {task.priority && (
              <span className={`w-1.5 h-1.5 rounded-full ${priorityColor}`} />
            )}
            {task.project && (
              <span className="text-xs text-text-tertiary truncate">
                {task.project}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * Renders a single event item
 */
function EventItem({
  event,
  time,
  onClick,
}: {
  event: SidebarEvent;
  time: Date;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors duration-100 cursor-pointer border-l-2 border-primary"
    >
      <div className="flex items-start gap-3">
        {/* Calendar icon */}
        <div className="mt-0.5 flex-shrink-0">
          <svg
            className="w-4 h-4 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary truncate">{event.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs font-mono text-text-tertiary">
              {event.allDay ? "All day" : formatTime(time)}
            </span>
            {event.type && (
              <span className="text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                {event.type}
              </span>
            )}
            {event.location && (
              <span className="text-xs text-text-tertiary truncate">
                @ {event.location}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * Renders the AI-prioritized view with MUST-DO and SHOULD-DO sections
 */
function PrioritizedView({
  todayPlan,
  events,
  profile,
  onTaskClick,
  onEventClick,
  onOpenChat,
  onOpenWizard,
}: {
  todayPlan: TodayPlan;
  events: SidebarEvent[];
  profile?: UserProfile | null;
  onTaskClick: (task: SidebarTask) => void;
  onEventClick: (event: SidebarEvent) => void;
  onOpenChat?: () => void;
  onOpenWizard?: () => void;
}): React.ReactElement {
  const todayEvents = events.filter((event) => isToday(event.startTime));
  const hasContent = todayPlan.mustDo.length > 0 || todayPlan.shouldDo.length > 0 || todayEvents.length > 0;

  if (!hasContent) {
    if (profile && !profile.onboardingComplete && onOpenChat && onOpenWizard) {
      return <WelcomeCard profile={profile} onOpenChat={onOpenChat} onOpenWizard={onOpenWizard} />;
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center">
          <svg
            className="w-6 h-6 text-text-tertiary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
            />
          </svg>
        </div>
        <p className="text-text-tertiary text-sm">Nothing scheduled for today</p>
        <p className="text-text-tertiary text-xs">Use the AI to add tasks or events</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Warnings */}
      {todayPlan.warnings.length > 0 && (
        <div className="space-y-1">
          {todayPlan.warnings.map((warning, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
            >
              <svg
                className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
              <p className="text-xs text-yellow-500">{warning}</p>
            </div>
          ))}
        </div>
      )}

      {/* MUST-DO section */}
      {todayPlan.mustDo.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider px-3 py-1">
            MUST-DO
          </h3>
          <div className="space-y-1">
            {todayPlan.mustDo.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                showReasoning
                onClick={() => onTaskClick(task)}
              />
            ))}
          </div>
        </div>
      )}

      {/* SHOULD-DO section */}
      {todayPlan.shouldDo.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider px-3 py-1">
            SHOULD-DO
          </h3>
          <div className="space-y-1">
            {todayPlan.shouldDo.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                showReasoning
                onClick={() => onTaskClick(task)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Today's events */}
      {todayEvents.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider px-3 py-1">
            EVENTS
          </h3>
          <div className="space-y-1">
            {todayEvents.map((event) => (
              <EventItem
                key={event.id}
                event={event}
                time={new Date(event.startTime)}
                onClick={() => onEventClick(event)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Renders the fallback timeline view (original behavior)
 */
function TimelineView({
  tasks,
  events,
  profile,
  onTaskClick,
  onEventClick,
  onOpenChat,
  onOpenWizard,
}: {
  tasks: SidebarTask[];
  events: SidebarEvent[];
  profile?: UserProfile | null;
  onTaskClick: (task: SidebarTask) => void;
  onEventClick: (event: SidebarEvent) => void;
  onOpenChat?: () => void;
  onOpenWizard?: () => void;
}): React.ReactElement {
  // Filter today's tasks: due today, scheduled for today, or status is "today"/"in_progress"
  const todayTasks = tasks.filter((task) => {
    if (task.status === "done" || task.status === "archived") return false;
    if (task.dueDate && isToday(task.dueDate)) return true;
    if (task.scheduledDate && isToday(task.scheduledDate)) return true;
    return task.status === "today" || task.status === "in_progress";
  });

  // Filter today's events
  const todayEvents = events.filter((event) => isToday(event.startTime));

  // Build unified timeline
  const timeline: TimelineItem[] = [
    ...todayTasks.map((task): TimelineItem => ({
      type: "task",
      data: task,
      time: task.dueDate ? new Date(task.dueDate) : (task.scheduledDate ? new Date(task.scheduledDate) : null),
    })),
    ...todayEvents.map((event): TimelineItem => ({
      type: "event",
      data: event,
      time: new Date(event.startTime),
    })),
  ];

  // Sort by time (items without time go to bottom)
  timeline.sort((a, b) => {
    if (!a.time && !b.time) return 0;
    if (!a.time) return 1;
    if (!b.time) return -1;
    return a.time.getTime() - b.time.getTime();
  });

  if (timeline.length === 0) {
    if (profile && !profile.onboardingComplete && onOpenChat && onOpenWizard) {
      return <WelcomeCard profile={profile} onOpenChat={onOpenChat} onOpenWizard={onOpenWizard} />;
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center">
          <svg
            className="w-6 h-6 text-text-tertiary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
            />
          </svg>
        </div>
        <p className="text-text-tertiary text-sm">Nothing scheduled for today</p>
        <p className="text-text-tertiary text-xs">Use the AI to add tasks or events</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {timeline.map((item) => {
        if (item.type === "task") {
          return (
            <TaskItem
              key={`task-${item.data.id}`}
              task={item.data}
              time={item.time}
              onClick={() => onTaskClick(item.data)}
            />
          );
        } else {
          return (
            <EventItem
              key={`event-${item.data.id}`}
              event={item.data}
              time={item.time}
              onClick={() => onEventClick(item.data)}
            />
          );
        }
      })}
    </div>
  );
}

export function TodayPanel({
  tasks,
  events,
  todayPlan,
  profile,
  onTaskClick,
  onEventClick,
  onOpenChat,
  onOpenWizard,
}: TodayPanelProps): React.ReactElement {
  // If we have a todayPlan from the API, show the prioritized view
  if (todayPlan) {
    return (
      <PrioritizedView
        todayPlan={todayPlan}
        events={events}
        profile={profile}
        onTaskClick={onTaskClick}
        onEventClick={onEventClick}
        onOpenChat={onOpenChat}
        onOpenWizard={onOpenWizard}
      />
    );
  }

  // Fallback to timeline view
  return (
    <TimelineView
      tasks={tasks}
      events={events}
      profile={profile}
      onTaskClick={onTaskClick}
      onEventClick={onEventClick}
      onOpenChat={onOpenChat}
      onOpenWizard={onOpenWizard}
    />
  );
}
